import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { AuthApi, type CurrentUser } from "../api/auth";
import { tokenStorage } from "../api/client";

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SSO bootstrap (Wave K): даже если localStorage пуст, у пользователя
    // может быть валидная HttpOnly cookie `.veyuhub.com/vy_access` после
    // логина в cabinet/crm. Пробуем /users/me напрямую -- 200 значит
    // сессия живая.
    AuthApi.me()
      .then((res) => {
        if (res.data.role === "ADMIN" || res.data.role === "MODERATOR") {
          setUser(res.data);
        } else {
          tokenStorage.clear();
        }
      })
      .catch(() => tokenStorage.clear())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const res = await AuthApi.login(email, password);
    tokenStorage.set(res.data.access_token);
    const me = await AuthApi.me();
    if (me.data.role !== "ADMIN" && me.data.role !== "MODERATOR") {
      tokenStorage.clear();
      throw new Error("Доступ только для администраторов и модераторов");
    }
    setUser(me.data);
  };

  const logout = async () => {
    // Сначала чистим SSO-cookie на сервере, потом локальный токен.
    try {
      const { apiClient } = await import("../api/client");
      await apiClient.post("/auth/logout").catch(() => undefined);
    } catch {
      /* offline ok */
    }
    tokenStorage.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth должен использоваться внутри AuthProvider");
  return ctx;
};
